import { Component, Input, computed, inject, signal } from '@angular/core';

import { SAVING_ARENA_API_CONFIG } from '../../core/api/api.config';
import { FrontendProduct } from '../../core/api/api.models';
import { ProductCard } from '../product-card/product-card';

interface ProductCardItem {
  /** Product id — the card needs it for the `/go/p/:id` Buy Now redirect. */
  id: string;
  title: string;
  imageUrl: string;
  hoverImageUrl: string;
  storeName: string;
  storeInitial: string;
  storeLogoUrl: string;
  storePath: string;
  /** Whether the product goes off-site, so the card can build its own /go/p/ redirect. */
  isExternal: boolean;
  category: string;
  description: string;
  price: string;
  oldPrice: string;
  /** In-app product page for "View Detail"; '' when the product has no slug. */
  detailPath: string;
}

// Shared product grid built on the canonical `app-product-card` design. Feed it serialized
// products and it renders the same cards anywhere: <app-product-grid [products]="..." />
@Component({
  selector: 'app-product-grid',
  imports: [ProductCard],
  templateUrl: './product-grid.html',
  styleUrl: './product-grid.scss',
})
export class ProductGrid {
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly source = signal<FrontendProduct[]>([]);

  @Input() set products(value: FrontendProduct[] | null | undefined) {
    this.source.set(Array.isArray(value) ? value : []);
  }

  protected readonly items = computed<ProductCardItem[]>(() =>
    this.source()
      .map((product) => this.toItem(product))
      .filter((product): product is ProductCardItem => product !== null),
  );

  private toItem(product: FrontendProduct): ProductCardItem | null {
    const title = (product.name || product.title || '').trim();
    if (!title) {
      return null;
    }

    const store = product.store;
    const storeName = store && typeof store.name === 'string' ? store.name.trim() : '';
    const storeLogo =
      store && typeof store.logoImagePath === 'string' ? store.logoImagePath.trim() : '';
    const storeSlug = store && typeof store.slug === 'string' ? store.slug.trim() : '';

    const mainImage = product.thumbnailImagePath || '';
    // First alternate gallery angle for the card's hover cross-fade; '' when there's no real second
    // image, so the card simply keeps the main one.
    const hoverImage =
      (product.galleryImagePaths ?? []).find((path) => path && path !== mainImage) || '';

    const slug = typeof product.slug === 'string' ? product.slug.trim() : '';

    return {
      id: (product.id ?? '').trim(),
      title,
      imageUrl: mainImage ? this.assetUrl(mainImage) : '',
      hoverImageUrl: hoverImage ? this.assetUrl(hoverImage) : '',
      storeName,
      storeInitial: storeName ? storeName.charAt(0).toUpperCase() : '',
      storeLogoUrl: storeLogo ? this.assetUrl(storeLogo) : '',
      storePath: storeSlug ? `/${storeSlug}` : '',
      isExternal: product.isExternal === true,
      category:
        product.category && typeof product.category.name === 'string' ? product.category.name : '',
      description: this.stripHtml(product.description ?? ''),
      price: (product.newPrice || '').trim(),
      oldPrice: (product.oldPrice || '').trim(),
      detailPath: slug ? `/${slug}` : '',
    };
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private assetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }
    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }
}
